/**
 * Sync API — enqueue and read rows in the shared `scraper_jobs` table.
 *
 * The backend is a producer/reader only: it never runs Telegram work itself,
 * so every call here is either an enqueue or a poll. All timestamps come back
 * as ISO strings carrying the server's UTC offset — `new Date(...)` parses
 * them correctly in any browser timezone.
 */

import apiClient from './client';

/**
 * Job types this backend accepts on POST /sync/run/{job_type}.
 * `sync_dialogs` refreshes one account's Telegram channel membership; it is
 * account-wide by definition (pinned to an account_id, NULL target).
 */
export type RunnableJobType = 'find_missing' | 'find_new' | 'insert_missing' | 'sync_dialogs';

export const RUNNABLE_JOB_TYPES: RunnableJobType[] = [
  'find_missing',
  'find_new',
  'insert_missing',
  'sync_dialogs',
];

/** The `job_type` used for links queued through POST /sync/join. */
export const JOIN_JOB_TYPE = 'join_channel';

export type JobStatus = 'queued' | 'running' | 'completed' | 'failed' | 'canceled';

/** `status`, plus `skipped` for a completed job whose result says it soft-skipped. */
export type DisplayStatus = JobStatus | 'skipped';

export type JobPayload = Record<string, unknown>;

export interface SyncJob {
  id: number;
  job_type: string;
  status: JobStatus;
  display_status: DisplayStatus;
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
  params: JobPayload | null;
  progress: JobPayload | null;
  result: JobPayload | null;
  created_by: number | null;
  /** Which manuscript scraper account the row is queued for; nullable in the DB. */
  account_id: number | null;
  /** Only on queued join_channel rows; null when joins are disabled by config. */
  estimated_at: string | null;
}

export interface ExistingJobRef {
  id: number;
  status: string;
  created_at: string | null;
}

export interface RunJobResult {
  ok: boolean;
  id: number | null;
  duplicate: boolean;
  existing: ExistingJobRef | null;
}

export interface QueuedJoin {
  link: string;
  id: number;
}

export interface JoinLinkError {
  link: string;
  reason: string;
}

export interface JoinResult {
  /** The account these jobs were queued for — echoed back, never inferred. */
  account_id: number;
  queued: QueuedJoin[];
  duplicates: string[];
  /** Rare concurrent-race bucket: a competing producer won the unique key. */
  blocked_by_dedup: string[];
  errors: JoinLinkError[];
}

export interface CancelResult {
  ok: boolean;
  id: number;
  status: string;
}

/**
 * A manuscript-scoped scraper account. `channel_count` counts only channels,
 * groups and supergroups — private/bot chats do not eat into Telegram's
 * per-account ceiling, so they are not counted here either.
 */
export interface SyncAccount {
  id: number;
  label: string;
  phone: string | null;
  status: string;
  role: string;
  /** The account run_job sweeps with, and the join dialog's pre-selection. */
  is_default: boolean;
  channel_count: number;
  channel_limit: number;
  headroom: number;
  /** null = never verified (no allowlist rows, or none of them verified). */
  last_verified_at: string | null;
  last_connected_at: string | null;
}

/** Server-side cap on POST /sync/join. */
export const MAX_JOIN_LINKS = 50;

export const listSyncAccounts = async (): Promise<SyncAccount[]> => {
  const res = await apiClient.get<SyncAccount[]>('/sync/accounts');
  return res.data;
};

/**
 * `targetChatsId` (a `Chats.id`) narrows the run to ONE channel and travels in
 * the BODY, not the query string. Only the per-channel sweep types take it —
 * `find_missing`, `find_new`, `insert_missing`. Passing it with `sync_dialogs`
 * is a 400, as is a channel that does not exist or is outside the clamped
 * account's allowlist. null/undefined = the account-wide run.
 */
export const runJob = async (
  jobType: RunnableJobType,
  params?: JobPayload,
  targetChatsId?: number | null
): Promise<RunJobResult> => {
  const body: { params?: JobPayload; target_chats_id?: number } = {};
  if (params) body.params = params;
  if (targetChatsId != null) body.target_chats_id = targetChatsId;
  const res = await apiClient.post<RunJobResult>(
    `/sync/run/${jobType}`,
    Object.keys(body).length > 0 ? body : undefined
  );
  return res.data;
};

export const joinChannels = async (links: string[], accountId: number): Promise<JoinResult> => {
  const res = await apiClient.post<JoinResult>('/sync/join', { links, account_id: accountId });
  return res.data;
};

/** Omitting `accountId` lists jobs across every manuscript account, newest first. */
export const listJobs = async (
  jobType?: string,
  limit = 50,
  accountId?: number
): Promise<SyncJob[]> => {
  const res = await apiClient.get<SyncJob[]>('/sync/jobs', {
    params: {
      limit,
      ...(jobType ? { job_type: jobType } : {}),
      ...(accountId != null ? { account_id: accountId } : {}),
    },
  });
  return res.data;
};

export const cancelJob = async (jobId: number): Promise<CancelResult> => {
  const res = await apiClient.post<CancelResult>(`/sync/jobs/${jobId}/cancel`);
  return res.data;
};

/** Queued or running — the states worth polling fast for. */
export const isJobActive = (job: SyncJob): boolean =>
  job.status === 'queued' || job.status === 'running';
